import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import { v4 as uuidv4 } from 'uuid';

import '@geoman-io/leaflet-geoman-free';
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css';

function EditableRoadNetwork({ onSaved }) {
    const map = useMap();
    const drawnItemsRef = useRef(new L.FeatureGroup());
    const [loaded, setLoaded] = useState(false);
    const [allFeatures, setAllFeatures] = useState([]);

    const CATEGORY_SUBCATEGORIES = {
        food: [
            { value: 'bar',       label: 'Bar' },
            { value: 'restaurant', label: 'Restaurant' },
            { value: 'cafe', label: 'Cafe' },
            { value: 'fastfood',   label: 'Fast Food' },
        ],
        sport: [
            { value: 'sport',       label: 'Sport' },
            { value: 'health', label: 'Health' },
            { value: 'beauty',   label: 'Beauty' },
        ],
    };


    const getColorByRoadType = (roadType) => {
        switch (roadType) {
            case 'car':        return 'blue';
            case 'pedestrian': return 'red';
            case 'both':       return 'green';
            default:           return 'gray';
        }
    };

    // Функция обновления иконки маркера
    const updateMarkerIcon = (layer) => {
        const markerIconName = layer.feature?.properties?.markerIconName;
        let iconUrl = markerIconName
            ? `/icons/round_logo/${markerIconName}.png`
            : `/icons/marker.svg`;

        const img = new Image();
        img.onload = () => {
            layer.setIcon(new L.Icon({
                iconUrl,
                iconSize: [25, 41],
                iconAnchor: [12, 41],
                popupAnchor: [1, -34],
                shadowUrl: null
            }));
        };
        img.onerror = () => {
            layer.setIcon(new L.Icon({
                iconUrl: `/icons/marker.svg`,
                iconSize: [25, 41],
                iconAnchor: [12, 41],
                popupAnchor: [1, -34],
                shadowUrl: null
            }));
        };
        img.src = iconUrl;
    };

    const createRoadPopupHtml = (layer) => {
        const props = layer.feature?.properties || {};
        const currentName = props.name || '';
        const currentType = props.roadType || 'both';
        return `
      <div style="min-width: 150px;">
        <div style="margin-bottom: 5px;">
          <label for="street-name">Название улицы:</label><br/>
          <input type="text" id="street-name" value="${currentName}" />
        </div>
        <div style="margin-bottom: 5px;">
          <label for="road-type">Тип дороги:</label><br/>
          <select id="road-type">
            <option value="car" ${currentType === 'car' ? 'selected' : ''}>Только авто</option>
            <option value="pedestrian" ${currentType === 'pedestrian' ? 'selected' : ''}>Только пешеходы</option>
            <option value="both" ${currentType === 'both' ? 'selected' : ''}>Обе категории</option>
          </select>
        </div>
        <button id="save-road-props" type="button">Сохранить</button>
      </div>
    `;
    };

    const displayTagCloud = (container, tags) => {
        if (!tags || !tags.length) return;

        const tagsArray = tags.split(' ').filter(tag => tag.trim() !== '');
        if (tagsArray.length === 0) return;

        const tagCloudContainer = document.createElement('div');
        tagCloudContainer.className = 'tag-cloud';
        tagCloudContainer.style.marginTop = '10px';

        tagsArray.forEach(tag => {
            const tagElement = document.createElement('span');
            tagElement.textContent = tag;
            tagElement.style.display = 'inline-block';
            tagElement.style.backgroundColor = '#f0f0f0';
            tagElement.style.padding = '2px 8px';
            tagElement.style.borderRadius = '10px';
            tagElement.style.margin = '2px';
            tagElement.style.fontSize = '12px';
            tagCloudContainer.appendChild(tagElement);
        });

        container.appendChild(tagCloudContainer);
    };

    const createMarkerPopupHtml = (layer) => {
        const props = layer.feature?.properties || {};
        const currentName = props.name || '';
        const currentCategory = props.category || 'Other';
        const currentStart = props.operatingHours ? props.operatingHours.start : '';
        const currentEnd = props.operatingHours ? props.operatingHours.end : '';
        const currentIconName = props.markerIconName || '';
        const cost = props.cost || '';
        const tagsValue = props.tags || '';
        const currentSubcat    = props.subcategory || '';

        return `
      <div style="min-width: 150px;">
        <div style="margin-bottom: 5px;">
          <label for="marker-name">Имя:</label><br/>
          <input type="text" id="marker-name" value="${currentName}" />
        </div>
        <div style="margin-bottom:5px;">
         <label><input type="checkbox" id="is-parent" ${props.isParent ? 'checked' : ''}/> Родительский объект</label>
        </div>
        <div id="children-container" style="margin-bottom:5px; display:${props.isParent ? 'block' : 'none'};">
         <strong>Дочерние объекты:</strong>
         <ul id="children-list" style="max-height:100px; overflow:auto;"></ul>
       </div>
        <div style="margin-bottom: 5px;">
          <label for="marker-category">Категория:</label><br/>
          <select id="marker-category">
            
            <option value="beaches" ${currentCategory === 'beaches' ? 'selected' : ''}>1. Beach Clubs</option>
            <option value="hotel" ${currentCategory === 'hotel' ? 'selected' : ''}>2. Hotels</option>
            <option value="entertainment" ${currentCategory === 'entertainment' ? 'selected' : ''}>3. Entartaiments</option>
            <option value="bank" ${currentCategory === 'bank' ? 'selected' : ''}>4. Banks & ATM</option>
            <option value="society" ${currentCategory === 'society' ? 'selected' : ''}>5. Society & Services</option>
            <option value="sport" ${currentCategory === 'sport' ? 'selected' : ''}>6. Sport, Health, Beauty</option>
            <option value="food" ${currentCategory === 'food' ? 'selected' : ''}>7. Restaurant</option>
            <option value="shop" ${currentCategory === 'shop' ? 'selected' : ''}>8. Shops</option>
            <option value="education" ${currentCategory === 'education' ? 'selected' : ''}>9. Education</option>
            <option value="estate" ${currentCategory === 'estate' ? 'selected' : ''}>10. Real Estate</option>
            <option value="other" ${currentCategory === 'other' ? 'selected' : ''}>0. Addition</option>
            <option value="panorama" ${currentCategory === 'panorama' ? 'selected' : ''}>DEV. Panorama</option>
            
          </select>
        </div>

         <div id="subcategory-container"
              style="margin-bottom:5px; display:none;">
           <label for="marker-subcategory">Подкатегория:</label><br/>
           <select id="marker-subcategory">
           </select>
+        </div>

        <div style="margin-bottom: 5px;">
          <label for="marker-icon-name">Имя картинки:</label><br/>
          <input type="text" id="marker-icon-name" value="${currentIconName}" />
        </div>
        <div style="margin-bottom: 5px;">
          <label for="marker-name">Средний чек в Манатах:</label><br/>
          <input type="text" id="cost" value="${cost}" />
        </div>
        <div style="margin-bottom: 5px;">
          <label for="tags">Теги для поиска</label><br/>
          <input type="text" id="tags" value="${tagsValue}" placeholder="Например: вид на море греческая итальянская" />
        </div>
        <div style="margin-bottom: 5px;">
          <label>Время работы:</label><br/>
          <input type="time" id="operating-start" value="${currentStart}" /> -
          <input type="time" id="operating-end" value="${currentEnd}" />
        </div>
        <button id="save-marker-props" type="button">Сохранить</button>
      </div>
    `;
    };

    // Обернули в useCallback для стабильности ссылки
    const handleRoadPopupOpen = useCallback((e) => {
        const layer = e.target;
        const container = e.popup.getElement();
        const saveBtn = container.querySelector('#save-road-props');
        const nameInput = container.querySelector('#street-name');
        const typeSelect = container.querySelector('#road-type');

        const isParentCheckbox = container.querySelector('#is-parent');
        const childrenContainer  = container.querySelector('#children-container');
        const childrenList       = container.querySelector('#children-list');

        // Рендерим список «дочек»
        function renderChildren() {
            childrenList.innerHTML = '';
            allFeatures
                .filter(f => f.id !== layer.feature.id)
                .forEach(f => {
                    const li = document.createElement('li');
                    li.textContent = `${f.id} — ${f.properties.name}`;
                    li.style.cursor = 'pointer';
                    if ((layer.feature.properties.children||[]).includes(f.id)) {
                        li.style.fontWeight = 'bold';
                    }
                    li.addEventListener('click', () => {
                        const setKids = new Set(layer.feature.properties.children || []);
                        if (setKids.has(f.id)) setKids.delete(f.id);
                        else setKids.add(f.id);
                        layer.feature.properties.children = Array.from(setKids);
                        renderChildren();
                    });
                    childrenList.appendChild(li);
                });
        }

        isParentCheckbox.addEventListener('change', ev => {
            const yes = ev.target.checked;
            layer.feature.properties.isParent = yes;
            if (yes) {
                layer.feature.properties.children = layer.feature.properties.children || [];
                childrenContainer.style.display = 'block';
                renderChildren();
            } else {
                childrenContainer.style.display = 'none';
                delete layer.feature.properties.children;
            }
        });

        // если уже родитель — сразу отрисовать
        if (layer.feature.properties.isParent) {
            renderChildren();
        }

        if (saveBtn) {
            saveBtn.addEventListener('click', () => {
                const newName = nameInput.value;
                const newType = typeSelect.value;
                if (!layer.feature) {
                    layer.feature = { type: 'Feature',
                        properties: {},
                        id: uuidv4()
                    };
                }
                layer.feature.properties.name = newName;
                layer.feature.properties.roadType = newType;
                layer.setStyle({ color: getColorByRoadType(newType) });
                layer.closePopup();
            });
        }
    }, []);

    const handleMarkerPopupOpen = useCallback((e) => {
        const layer = e.target;
        const container = e.popup.getElement();
        const saveBtn = container.querySelector('#save-marker-props');
        const nameInput = container.querySelector('#marker-name');
        const categorySelect = container.querySelector('#marker-category');
        const subcatContainer = container.querySelector('#subcategory-container');
        const subcatSel     = container.querySelector('#marker-subcategory');
        const iconNameInput = container.querySelector('#marker-icon-name');
        const startInput = container.querySelector('#operating-start');
        const endInput = container.querySelector('#operating-end');
        const costInput = container.querySelector('#cost'); // Получаем поле стоимости
        const tagsInput = container.querySelector('#tags'); // Получаем поле тегов

        // Показываем облако тегов, если есть теги
        if (layer.feature?.properties?.tags) {
            displayTagCloud(container, layer.feature.properties.tags);
        }

        // получаем текущие props
        const currentProps  = layer.feature.properties || {};
        const initCategory  = currentProps.category || '';
        const initSubcat    = currentProps.subcategory || '';

        // функция для (пере)заполнения подкатегорий
        const populateSubcats = (cat) => {
            const list = CATEGORY_SUBCATEGORIES[cat];
            if (list && list.length) {
                // очищаем и вставляем новые опции
                subcatSel.innerHTML = '';         list.forEach(opt => {
                    const o = document.createElement('option');
                    o.value = opt.value;
                    o.textContent = opt.label;
                    if (opt.value === initSubcat) o.selected = true;
                    subcatSel.appendChild(o);
                });
                subcatContainer.style.display = 'block';
            } else {
                subcatContainer.style.display = 'none';
            }
        };

        // при открытии — заполнить подкатегории под текущую категорию
        populateSubcats(initCategory);

        // слушаем изменение категории, чтобы переключаться на лету
        categorySelect.addEventListener('change', (ev) => {
            populateSubcats(ev.target.value);
        });

        if (saveBtn) {
            saveBtn.addEventListener('click', () => {
                const newName = nameInput.value;
                const newCategory = categorySelect.value;
                const newSubcat   = subcatSel && subcatSel.value;
                const newIconName = iconNameInput.value.trim();
                const newStart = startInput.value;
                const newEnd = endInput.value;
                const newCost = costInput.value; // Используем значение поля стоимости
                const newTags = tagsInput.value; // Получаем значение тегов

                if (!layer.feature) {
                    layer.feature = { type: 'Feature', properties: {} };
                }
                layer.feature.properties.name = newName;
                layer.feature.properties.category = newCategory;

                if (newSubcat) {
                    layer.feature.properties.subcategory = newSubcat;
                } else {
                    delete layer.feature.properties.subcategory;
                }

                layer.feature.properties.markerIconName = newIconName;
                layer.feature.properties.operatingHours = { start: newStart, end: newEnd };
                layer.feature.properties.cost = newCost;
                layer.feature.properties.tags = newTags; // Сохраняем теги

                updateMarkerIcon(layer);
                layer.closePopup();
            });
        }
    }, []);

    useEffect(() => {
        drawnItemsRef.current.addTo(map);

        fetch('/api/roads')
            .then((res) => res.json())
            .then((data) => {
                drawnItemsRef.current.clearLayers();
                setAllFeatures(data.features);

                L.geoJSON(data, {
                    onEachFeature: (feature, layer) => {
                        if (feature.geometry.type === 'LineString') {
                            const color = getColorByRoadType(feature.properties?.roadType);
                            layer.setStyle?.({ color });
                            layer.bindPopup(createRoadPopupHtml(layer));
                            layer.on('popupopen', handleRoadPopupOpen);
                        } else if (feature.geometry.type === 'Point') {
                            layer.bindPopup(createMarkerPopupHtml(layer));
                            layer.on('popupopen', handleMarkerPopupOpen);
                            updateMarkerIcon(layer);
                        }
                        drawnItemsRef.current.addLayer(layer);
                    },
                });
                setLoaded(true);
            })
            .catch((err) => console.error('Error loading roads:', err));
    }, [map, handleRoadPopupOpen, handleMarkerPopupOpen]);

    useEffect(() => {
        if (!loaded) return;

        map.pm.addControls({
            position: 'topleft',
            drawMarker: true,
            drawPolyline: true,
            drawPolygon: false,
            editMode: true,
            dragMode: false,
            removalMode: true,
        });

        map.pm.setGlobalOptions({
            snappable: true,
            snapDistance: 20,
        });

        const handleCreate = (e) => {
            const layer = e.layer;
            if (layer instanceof L.Marker) {
                if (!layer.feature) {
                    layer.feature = { type: 'Feature', properties: {} };
                }
                layer.feature.properties.name = '';
                layer.feature.properties.category = 'Others';
                layer.feature.properties.markerIconName = '';
                layer.feature.properties.operatingHours = { start: '08:00', end: '20:00' };
                layer.feature.properties.cost = '';
                layer.feature.properties.tags = '';
                layer.feature.properties.children = [];
                layer.feature.properties.isParent = false;
                layer.bindPopup(createMarkerPopupHtml(layer));
                layer.on('popupopen', handleMarkerPopupOpen);
                drawnItemsRef.current.addLayer(layer);
                layer.openPopup();
                //TODO: сделать изменение направления дороги
            } else if (layer instanceof L.Polyline) {
                if (!layer.feature) {
                    layer.feature = { type: 'Feature', properties: {} };
                }
                layer.feature.properties.name = '';
                layer.feature.properties.roadType = 'both';
                layer.setStyle({ color: getColorByRoadType('both') });
                layer.bindPopup(createRoadPopupHtml(layer));
                layer.on('popupopen', handleRoadPopupOpen);
                drawnItemsRef.current.addLayer(layer);
                layer.openPopup();
            }
        };

        const handleRemove = (e) => {
            drawnItemsRef.current.removeLayer(e.layer);
        };

        map.on('pm:create', handleCreate);
        map.on('pm:remove', handleRemove);

        return () => {
            map.off('pm:create', handleCreate);
            map.off('pm:remove', handleRemove);
        };
    }, [map, loaded, handleRoadPopupOpen, handleMarkerPopupOpen]);

    const saveRoads = () => {
        const geojson = drawnItemsRef.current.toGeoJSON();
        fetch('/api/roads', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(geojson),
        })
            .then((res) => res.json())
            .then((data) => {
                if (onSaved) onSaved(data);
                alert('Данные успешно сохранены');
            })
            .catch((err) => console.error('Error saving roads:', err));
    };

    return (
        <div
            className="save-button"
            style={{
                position: 'absolute',
                top: 85,
                left: 20,
                zIndex: 1000,
                background: 'white',
                padding: '5px',
                borderRadius: '4px',
            }}
        >
            <button onClick={saveRoads}>Сохранить изменения</button>
        </div>
    );
}

export default EditableRoadNetwork;
